/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  OnDestroy,
} from '@angular/core';
import * as echarts from 'echarts/core';
import { CommonModule } from '@angular/common';
import { InsightsDataService } from '../../insights-data.service';
import { Subject } from 'rxjs';
import { frequencyDatasheetGraph } from '../../insights-data.interface';
@Component({
  selector: 'app-frequency-datasheet-graph',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './frequency-datasheet-graph.component.html',
  styleUrl: './frequency-datasheet-graph.component.scss',
})
export class FrequencyDatasheetGraphComponent implements OnDestroy {
  @Output() chartImage = new EventEmitter<string>();
  @Output() allChartImage = new EventEmitter<string[]>();
  private readonly destroy$ = new Subject<void>();
  chartInstance!: echarts.ECharts;
  chartInstances: any[] = [];
  weekGraphData: any = null;
  renderedChartsCount = 0;
  allChartsRendered = false;
  totalCharts = 0;
  isShow = true;
  maxLength = 15;
  splitRange = 15;
  behaviorId = 0;
  showTrendLine = false;
  @Input() set graphData(freqDatasheetData: frequencyDatasheetGraph) {
    this.behaviorId = freqDatasheetData?.caseBehaviorId ?? 0;
    this.showTrendLine = freqDatasheetData?.showTrendLine ?? false;
    this.setGraphOptionsDefault(freqDatasheetData, false);
    this.setGraphOptionsDefault(freqDatasheetData, true);
  }

  constructor(
    private elementRef: ElementRef,
    private insightService: InsightsDataService
  ) {}

  setGraphOptionsDefault(
    freqDatasheetData: frequencyDatasheetGraph,
    showDaysInterval: boolean
  ) {
    if (showDaysInterval) {
      this.chartInstances = [];
      this.totalCharts = 1;
      this.allChartsRendered = false;
      this.weekGraphData = [];
      const weekData = freqDatasheetData;
      const hasValidData =
        weekData.duration?.some(val => val !== null) ||
        weekData.frequency.some(val => val !== null) ||
        (weekData.treatmentPhases && weekData.treatmentPhases.length > 0);

      this.weekGraphData.push(weekData);

      const graphOptions = this.setGraphOptions(weekData);
      setTimeout(() => {
        const chartDom = document.getElementById(
          `chart-container-frequency-${this.behaviorId}-${0}`
        );

        if (chartDom) {
          const chartInstance = echarts.init(chartDom);
          chartInstance.setOption(graphOptions);

          chartInstance.on('finished', () => {
            this.renderedChartsCount++;

            if (this.renderedChartsCount === this.totalCharts) {
              this.allChartsRendered = true;

              this.exportAllGraphs();
            }
          });

          if (hasValidData) {
            this.chartInstances.push(chartInstance);
          }
        }
      }, 500);
    } else {
      const chartOptions = this.setGraphOptions(freqDatasheetData);

      const datasheetDom = this.elementRef.nativeElement.querySelector(
        '#frequency-datasheet'
      );

      if (datasheetDom) {
        const chartInstance = echarts.init(datasheetDom);
        this.chartInstance = chartInstance;
        chartInstance.setOption(chartOptions);
        chartInstance.on('finished', () => {
          this.emitChartAsImage();
        });
      }
    }
  }

  setGraphOptions(freqDatasheetData: frequencyDatasheetGraph) {
    const hasDataY0 = freqDatasheetData.duration?.some(
      val => val !== null && val !== undefined
    );

    const trendline = this.insightService.generateTrendlineArray(
      freqDatasheetData.xLabels,
      freqDatasheetData.frequency
    );

    const trendlineDuration = this.insightService.generateTrendlineArray(
      freqDatasheetData.xLabels,
      freqDatasheetData.duration || []
    );

    const trendLineSeries = this.showTrendLine
      ? [
          {
            name: 'Trendline Frequency',
            type: 'line',
            data: trendline,
            yAxisIndex: 1,
            symbol: 'none',
            lineStyle: {
              color: 'red',
              width: 2,
              type: 'dashed',
            },
            emphasis: { disabled: true },
            showSymbol: false,
            connectNulls: true,
            z: 10,
          },
          {
            name: 'Trendline Duration',
            type: 'line',
            data: trendlineDuration,
            yAxisIndex: 0,
            symbol: 'none',
            lineStyle: {
              color: 'blue',
              width: 2,
              type: 'dashed',
            },
            emphasis: { disabled: true },
            showSymbol: false,
            connectNulls: true,
            z: 10,
          },
        ]
      : [];

    const markLines = freqDatasheetData.treatmentPhases?.map(item => {
      const color = this.insightService.getRandomColor();
      return {
        xAxis: item.treatmentPhaseDate,
        label: {
          show: true,
          formatter: item.treatmentPhaseName,
          position: 'insideEndTop',
          color,
        },
        lineStyle: {
          type: 'solid',
          color,
          width: 2,
        },
        tooltip: {
          show: true,
          formatter: () =>
            `Treatment Phase <br>${item.dateLabel} - ${item.treatmentPhaseName}`,
        },
      };
    });

    const dataZoomConfig = [
      {
        type: 'inside',
        xAxisIndex: 0,

        zoomOnMouseWheel: true,

        moveOnMouseMove: true,
        moveOnMouseWheel: true,

        minValueSpan: null,
        maxValueSpan: null,

        start: 0,
        end: 100,
      },
    ];
    const datasheetGraphOption = {
      title: {
        text: 'Frequency/Duration Based Datasheet Graph',
        left: 'center',
      },

      tooltip: { trigger: 'item' },

      legend: {
        data: [
          'Frequency',
          'Duration in minutes',
          'Trendline Frequency',
          'Trendline Duration',
        ],
        left: 'left',
        bottom: 40,
        selectedMode: true,
      },

      grid: { left: '4%', right: '3%', containLabel: true },

      xAxis: {
        type: 'category',
        boundaryGap: true,
        data: freqDatasheetData.xLabels,
      },

      yAxis: [
        {
          type: 'value',
          name: hasDataY0 ? 'Duration in minutes' : '',
          splitLine: { show: hasDataY0 },
          axisLine: { show: true },
          nameLocation: 'center',
          nameTextStyle: { padding: [0, 0, 30, 0] },
        },
        {
          type: 'value',
          name: 'Frequency',
          position: 'right',
          splitLine: { show: !hasDataY0 },
          axisLine: { show: true },
          nameLocation: 'center',
          nameTextStyle: { padding: [30, 0, 0, 0] },
        },
      ],

      series: [
        {
          name: 'Duration in minutes',
          type: 'bar',
          data: freqDatasheetData.duration,
          barMaxWidth: 30,
          itemStyle: { color: '#47CD89', barBorderRadius: [10, 10, 0, 0] },
          label: { show: false },
        },
        {
          name: 'Frequency',
          type: 'line',
          data: freqDatasheetData.frequency,
          yAxisIndex: 1,
          symbolSize: 12,
          symbol: 'circle',
          showSymbol: true,
          showAllSymbol: true,
          itemStyle: { color: 'orange' },
          label: {
            show: true,
            position: 'top',
            formatter: '{c}',
            fontSize: 10,
          },
          connectNulls: true,
        },
        {
          name: 'Treatment Phase',
          type: 'line',
          markLine: { symbol: 'none', data: markLines },
        },
        ...trendLineSeries,
      ],

      dataZoom: dataZoomConfig,
    };

    return datasheetGraphOption;
  }

  exportAllGraphs() {
    if (!this.allChartsRendered) {
      return;
    }

    const images: string[] = [];

    this.chartInstances.forEach(chartInstance => {
      const base64Image =
        this.insightService.convertImagetoBase64(chartInstance);

      images.push(base64Image);
    });

    this.allChartImage.emit(images);
  }

  emitChartAsImage() {
    const chartImageBase64 = this.insightService.convertImagetoBase64(
      this.chartInstance
    );
    this.chartImage.emit(chartImageBase64);
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
